import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { modifyProps } from 'lp-hoc'
import * as Types from 'types'
import GoogleMap from 'google-map-react'
import { mapStyles as styles, useCommunity, mapBoundaryUrl } from 'utils'
import MapMarker from '../map-marker'
import ClusterMarker from '../map-marker/cluster-marker'
import { fitToLocations, findCenter, getClusters } from './helpers'
import { compact, groupBy, map, flatten, first } from 'lodash'
import { scroller } from 'react-scroll'

const MAX_CLUSTER_ZOOM = 18

const MAP_SIZE = { height: 500, width: 1000 }
const DEFAULT_ZOOM = 8

const propTypes = {
  locations: PropTypes.arrayOf(Types.location),
  autoZoom: PropTypes.bool,
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  height: PropTypes.number,
  hidePopup: PropTypes.bool,
  selectedLocation: Types.location,
  setSelectedLocation: PropTypes.func.isRequired,
  width: PropTypes.number,
  zoom: PropTypes.number,
  noScroll: PropTypes.bool,
  cluster: PropTypes.bool,
}

const defaultProps = {
  locations: [],
  height: MAP_SIZE.height,
  autoZoom: false,
  hidePopup: false,
  selectedLocation: null,
  width: null,
  zoom: DEFAULT_ZOOM,
  noScroll: false,
  cluster: false,
}

function BaseMapDisplay({
  height,
  locations,
  selectedLocation,
  setSelectedLocation,
  hidePopup,
  width,
  noScroll,
  cluster,
}) {
  const hasCustomWidth = width !== MAP_SIZE.width

  const community = useCommunity()
  const isMichigan = community.subdomain === 'michigan'
  // SWPA uses a single brand-cyan color for all numbered cluster circles instead
  // of the default count-based teal scaling that every other community uses.
  const clusterMarkerColor = community.subdomain === 'swpa' ? '#00AEEF' : null
  const { search } = community

  const boundaryUrl = mapBoundaryUrl(community.subdomain, false)
  const fullBoundaryUrl = mapBoundaryUrl(community.subdomain, true)
  const mapCenter = search.map.center

  const [fullBoundary, setFullBoundary] = useState(null)
  useEffect(() => {
    let isMounted = true
    fetch(fullBoundaryUrl)
      .then((r) => (r.ok ? r.json() : null))
      .then((geojson) => {
        if (!isMounted || !geojson) return
        if (
          geojson.type === 'FeatureCollection' &&
          Array.isArray(geojson.features) &&
          geojson.features.length
        )
          setFullBoundary(geojson.features[0])
        else setFullBoundary(geojson)
      })
      .catch(() => {})
    return () => {
      isMounted = false
    }
  }, [fullBoundaryUrl])

  const { center, zoom } = useMemo(() => {
    return fitToLocations(locations, { height, width }, fullBoundary)
  }, [locations, fullBoundary, height, width])

  // Cluster mode runs the map uncontrolled and tracks the live zoom so we can
  // recompute clusters as the user zooms/pans.
  const mapRef = useRef(null)
  const [liveZoom, setLiveZoom] = useState(null)
  const effectiveZoom = liveZoom != null ? liveZoom : zoom

  const clusters = useMemo(() => {
    if (!cluster) return null
    return getClusters(locations, effectiveZoom)
  }, [cluster, locations, effectiveZoom])

  // Refit the (uncontrolled) cluster map whenever the result set changes.
  // Depend on the primitive fit values (not the memoized object references,
  // which are recreated every render) so selecting a pin doesn't reset zoom.
  const centerLat = center && center.lat
  const centerLng = center && center.lng
  useEffect(() => {
    if (!cluster || !mapRef.current || centerLat == null || !zoom) return
    mapRef.current.setCenter({ lat: centerLat, lng: centerLng })
    mapRef.current.setZoom(zoom)
  }, [cluster, centerLat, centerLng, zoom])

  // Pan to a location selected from the results list (cluster map only).
  useEffect(() => {
    if (!cluster || !mapRef.current || !selectedLocation) return
    mapRef.current.panTo({
      lat: selectedLocation.latitude,
      lng: selectedLocation.longitude,
    })
  }, [cluster, selectedLocation])

  if (!center || !zoom) return null

  const renderMarker = (location, key) => (
    <MapMarker
      key={key}
      lat={location.latitude}
      lng={location.longitude}
      isSelected={
        !!selectedLocation &&
        location.resourceId === selectedLocation.resourceId
      }
      onClick={(selectedLocation) => {
        setSelectedLocation(selectedLocation)
        if (!selectedLocation || noScroll) return
        const elementId = `result-${selectedLocation.resourceId}`
        scroller.scrollTo(elementId, {
          containerId: 'search-results',
          smooth: true,
          offset: -15,
        })
      }}
      location={location}
      hidePopup={hidePopup}
    />
  )

  const zoomToCluster = ({ latitude, longitude }) => {
    if (!mapRef.current) return
    const nextZoom = Math.min(
      (effectiveZoom || DEFAULT_ZOOM) + 2,
      MAX_CLUSTER_ZOOM
    )
    mapRef.current.setZoom(nextZoom)
    mapRef.current.panTo({ lat: latitude, lng: longitude })
  }

  // Controlled (non-cluster) maps drive the view via center/zoom props.
  // Cluster maps run uncontrolled (default* props) and are steered imperatively
  // via mapRef so user zoom/pan isn't fought by re-renders.
  const viewProps = cluster
    ? { defaultCenter: center, defaultZoom: zoom }
    : {
        defaultCenter: mapCenter,
        defaultZoom: DEFAULT_ZOOM,
        center: findCenter(selectedLocation, center),
        zoom,
      }

  return (
    <div
      style={{
        height,
        overflow: 'hidden',
        width: hasCustomWidth ? width : '100%',
      }}
      className="google-map"
      data-cy="map-display"
    >
      <GoogleMap
        bootstrapURLKeys={{
          key: process.env.GOOGLE_MAPS_API_KEY,
        }}
        {...viewProps}
        onChange={
          cluster ? ({ zoom: newZoom }) => setLiveZoom(newZoom) : undefined
        }
        options={{ styles, clickableIcons: false, fullscreenControl: false }}
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
          map.data.loadGeoJson(boundaryUrl)
          if (isMichigan) {
            map.data.setStyle(function (feature) {
              var color = feature.getProperty('COLOR')

              return {
                fillColor: color,
                strokeWeight: 1,
              }
            })
          } else {
            map.data.setStyle(() => ({ fillColor: 'gray', strokeWeight: 1 }))
          }

          // Add title for a11y
          map
            .getDiv()
            .querySelector('iframe')
            .setAttribute('title', 'Google Maps Display')
        }}
        yesIWantToUseGoogleMapApiInternals
      >
        {cluster
          ? clusters.map((c) =>
              c.count > 1 ? (
                <ClusterMarker
                  key={c.id}
                  lat={c.latitude}
                  lng={c.longitude}
                  count={c.count}
                  color={clusterMarkerColor}
                  onClick={() => zoomToCluster(c)}
                />
              ) : (
                renderMarker(c.points[0], c.id)
              )
            )
          : locations.map((location, i) => renderMarker(location, i))}
      </GoogleMap>
    </div>
  )
}

BaseMapDisplay.propTypes = propTypes
BaseMapDisplay.defaultProps = defaultProps

function setDefaultSize({ height, width }) {
  return {
    height: height || MAP_SIZE.height,
    width: width || MAP_SIZE.width,
  }
}

function formatSingleLocations(locations) {
  return locations.map((location) => {
    return {
      ...location,
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
    }
  })
}

function formatGroupedLocations(locations) {
  const location = first(locations)
  return {
    ...location,
    latitude: Number(location.latitude),
    longitude: Number(location.longitude),
    entries: locations,
  }
}

function formatLatLong(values, key) {
  const filteredLocations = values.filter((location) => location.latitude)
  if (filteredLocations.length === 0) return
  if (key === Types.UNIQUE_LOCATIONS_KEY || filteredLocations.length === 1)
    return formatSingleLocations(filteredLocations)
  return formatGroupedLocations(filteredLocations)
}

function formatLocations({ locations, cluster }) {
  // In cluster mode, keep locations individual (no ZIP pre-grouping) so the
  // clustering algorithm controls grouping based on zoom/proximity.
  if (cluster) {
    const withCoords = compact(locations).filter(
      (location) => location.latitude
    )
    return { locations: formatSingleLocations(withCoords) }
  }
  const groupedLocations = groupBy(compact(locations), (location) => {
    return !location.street ? location.zip : Types.UNIQUE_LOCATIONS_KEY
  })
  const formattedLocations = flatten(map(groupedLocations, formatLatLong))
  return {
    locations: compact(formattedLocations),
  }
}

export default compose(
  modifyProps(setDefaultSize),
  modifyProps(formatLocations)
)(BaseMapDisplay)
